import { useState } from 'react';

import { PlatformIcon } from '../../components/PlatformIcon.js';
import { BackdropPicker, WidgetPreview, useBackdrop } from './WidgetPreview.js';

/**
 * A compact style for gallery previews.
 *
 * Smaller text and a visible surface: at card size a fully transparent widget
 * with 1rem text is illegible, and a card that cannot be read sells nothing.
 * This is PRESENTATION for the gallery only — it is not a default, and it
 * never reaches a created widget's configuration.
 */
/**
 * Per-type preview configuration.
 *
 * An Event Label with no prefix previews as a bare name — "Kestrel" alone in a
 * pane, which tells an operator nothing about what the widget does. The
 * default a real one is created with has a prefix, so the preview shows one.
 */
function previewConfigFor(type: string): Record<string, unknown> {
  if (type === 'EVENT_LABELS') return { prefix: 'Latest follower: ', suffix: '' };
  return {};
}

const PREVIEW_STYLE = {
  theme: 'minimal',
  fontScale: 0.72,
  align: 'left',
  textShadow: true,
  cornerRadius: 6,
  backgroundOpacity: 0.55,
  spacing: 6,
  animate: false,
};
import { Tooltip } from '../../components/Tooltip.js';
import type { WidgetCatalogEntry } from '../../api/widgets.js';

/**
 * The widget gallery (ARCHITECTURE.md §103.2).
 *
 * ==================== WHAT A CARD IS ALLOWED TO CLAIM ====================
 * Only what the server said. `availablePlatforms` is the platforms this
 * workspace has connected that ALSO emit something the widget reads, computed
 * server-side from the emission table. A card with none of them says so and
 * offers no button — because a Follower Goal on a workspace with only Twitch
 * connected would be a bar that never moves, and §18 forbids offering it.
 *
 * There is no per-widget platform list in this file, and no capability table.
 * ========================================================================
 */

const CATEGORY_LABELS: Record<WidgetCatalogEntry['category'], string> = {
  CHAT: 'Chat',
  EVENTS: 'Events',
  GOALS: 'Goals',
  INFORMATION: 'Information',
};

export function WidgetGallery({
  catalog,
  mayManage,
  onCreate,
}: {
  catalog: readonly WidgetCatalogEntry[];
  mayManage: boolean;
  onCreate: (entry: WidgetCatalogEntry) => void;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  /*
   * One backdrop for the whole gallery, not one per card: an operator
   * comparing widgets is comparing them against the same background, and four
   * cards each on a different backdrop would be comparing nothing.
   */
  const [backdrop, setBackdrop] = useBackdrop('grid');
  const [category, setCategory] = useState<'ALL' | WidgetCatalogEntry['category']>('ALL');

  // Only categories Slice 1 actually has. An empty filter tab would be a
  // promise about a widget that does not exist.
  const categories = [...new Set(catalog.map((entry) => entry.category))];

  const shown = catalog.filter((entry) => {
    if (category !== 'ALL' && entry.category !== category) return false;
    const needle = query.trim().toLowerCase();
    if (needle === '') return true;
    return (
      entry.name.toLowerCase().includes(needle) ||
      entry.description.toLowerCase().includes(needle) ||
      CATEGORY_LABELS[entry.category].toLowerCase().includes(needle)
    );
  });

  return (
    <div className="widgets__gallery">
      <div className="widgets__filters">
        <label className="widgets__search">
          {/* Visually hidden, not absent: the placeholder is not a label. */}
          <span className="visually-hidden">Search widgets</span>
          <input
            type="search"
            className="field__input"
            value={query}
            placeholder="Search widgets…"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </label>

        <div className="widgets__categories" role="group" aria-label="Filter by category">
          {(['ALL', ...categories] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={`chip${category === value ? ' chip--active' : ''}`}
              aria-pressed={category === value}
              onClick={() => {
                setCategory(value);
              }}
            >
              {value === 'ALL' ? 'All' : CATEGORY_LABELS[value]}
            </button>
          ))}
        </div>

        {/*
          * The backdrop is a PREVIEW control, not a content filter, so it sits
          * apart from search and category with its own label. Mixed into the
          * same row it read as a fourth filter.
          */}
        <div className="widgets__preview-control">
          <span className="widgets__preview-label">Preview on</span>
          <BackdropPicker value={backdrop} onChange={setBackdrop} />
        </div>
      </div>

      <ul className="wcards">
        {shown.map((entry) => {
          const usable = entry.availablePlatforms.length > 0;

          return (
            <li
              key={entry.type}
              className={`wcard${usable ? '' : ' wcard--unavailable'}${
                entry.featured ? ' wcard--featured' : ''
              }`}
            >
              {/*
                * THE PREVIEW LEADS.
                *
                * A widget's output is the product, so the card shows what it
                * draws rather than an icon standing in for it. It is the same
                * renderer the browser source runs (§103.2), with synthetic
                * data — so a card cannot promise a look the widget does not
                * actually produce.
                */}
              <div className="wcard__preview">
                <WidgetPreview
                  widgetType={entry.type}
                  config={{
                    ...previewConfigFor(entry.type),
                    /*
                     * A featured card is wide enough to read at the scale the
                     * widget actually ships at, so it is not shrunk. Shrinking
                     * it would undersell the one card that exists to show what
                     * the product looks like.
                     */
                    style: entry.featured
                      ? { ...PREVIEW_STYLE, fontScale: 0.9, textOutline: true }
                      : PREVIEW_STYLE,
                  }}
                  backdrop={backdrop}
                  compact={!entry.featured}
                />
              </div>

              <div className="wcard__body">
                <div className="wcard__head">
                  <h3 className="wcard__title">{entry.name}</h3>
                  {entry.featured && (
                    /*
                     * A label, not only a size.
                     *
                     * §65: meaning must never rest on presentation alone. An
                     * operator who cannot perceive the card being wider still
                     * reads why it leads.
                     */
                    <span className="wcard__featured">Most popular</span>
                  )}
                  {entry.status === 'RESTRICTED' && (
                    /*
                     * A small marker, not a pill. At card width a full
                     * StatusPill took a third of the header and wrapped the
                     * title onto two lines — "Chat / Overlay" — which is worse
                     * than the information it was carrying. The conditions are
                     * explained in the tooltip and again in the editor, where
                     * they are actionable.
                     */
                    <Tooltip tip="Every message shows which platform it came from, Discord is not available for public overlays, and Twitch has its own rule about merged chat that you will be shown when you select it.">
                      <span className="wcard__conditions" tabIndex={0}>
                        Conditions
                      </span>
                    </Tooltip>
                  )}
                </div>

                <p className="wcard__text">{entry.description}</p>

                <div className="wcard__foot">
                  {usable ? (
                    <span className="wcard__platforms">
                      {entry.availablePlatforms.map((platform) => (
                        <PlatformIcon
                          key={platform}
                          platform={platform}
                          displayName={platform}
                          size={16}
                          label={platform}
                        />
                      ))}
                    </span>
                  ) : (
                    <Tooltip
                      tip={`Connaro receives ${entry.readsEvents.join(' or ')} from ${entry.supportedPlatforms.join(', ')}. None of those are connected to this workspace.`}
                    >
                      <span className="wcard__blocked">Nothing connected sends this</span>
                    </Tooltip>
                  )}

                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={!usable || !mayManage}
                    onClick={() => {
                      onCreate(entry);
                    }}
                  >
                    Create
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {shown.length === 0 && <p className="field__hint">No widget matches that search.</p>}
    </div>
  );
}
